import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  establishGontijoSession,
  hydrateGontijoSession,
  revalidateGontijoSession,
  signOutGontijoSession,
  syncGontijoSessionFromOtherTab,
} from '@/actions/gontijoSession';
import {
  selectGontijoSession,
  selectLoginOnCheckoutEnabled,
} from 'selectors/gontijoSessionSelectors';
import { EXPIRY_BUFFER_MS } from '@/services/gontijo-session/gontijoSessionStorage';
import handleGontijoSessionUpdate from '@/services/gontijo-session/handleGontijoSessionUpdate';

const GontijoSessionContext = createContext(null);

/** The widget's pub/sub channel for the Gontijo session. */
const GONTIJO_SESSION_EVENT = 'gontijo_session_widget';

/** The longest delay `setTimeout` holds without overflowing to an immediate fire. */
const MAX_TIMER_MS = 2 ** 31 - 1;

/** What every brand without checkout login reads: one object, so nothing downstream re-renders. */
const NO_SESSION = Object.freeze({
  token: null,
  expiresAt: null,
  email: null,
  isHydrated: false,
  isValidating: false,
  isAuthenticated: false,
});

/**
 * The session as the provider subscribes to it.
 *
 * The provider wraps the whole app for every brand, so this runs on every dispatch everywhere.
 * With the flag off it answers the constant above without touching storage; with it on, the
 * fields are primitives and `shallowEqual` keeps an unchanged session from reading as a new one —
 * `selectGontijoSession` builds a fresh object each call, and without the comparison the context
 * value, and `PaymentButton` with it, would change on every action.
 * @param {Object} state - Redux state.
 * @returns {Object} The session.
 */
const selectProviderSession = (state) =>
  selectLoginOnCheckoutEnabled(state) ? selectGontijoSession(state) : NO_SESSION;

/**
 * Owns the Gontijo client session's lifecycle: read it at boot, confirm it, and hand it out.
 *
 * Confirming at boot is also what carries the session through 3D Secure. Cielo leaves the site and
 * comes back with a full navigation, so the return *is* a boot: storage survives it, the provider
 * mounts again, and the token is checked before anything spends it. No separate return path is
 * needed, which is the point of having chosen storage over an in-memory holder.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {React.ReactElement} The provider.
 */
export function GontijoSessionProvider({ children }) {
  const dispatch = useDispatch();
  const enabled = useSelector(selectLoginOnCheckoutEnabled);
  const session = useSelector(selectProviderSession, shallowEqual);
  const hasCheckedAtBoot = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    dispatch(hydrateGontijoSession());
  }, [dispatch, enabled]);

  // The widget announces sign-ins and sign-outs on its own channel, so a Gontijo session never
  // reaches the loyalty handlers. The bus exists by now: the widget module creates it on import,
  // which Header does before the app renders. `sub` returns the unsubscribe.
  useEffect(() => {
    if (!enabled) return undefined;

    return window.reservamosPubSub?.sub(GONTIJO_SESSION_EVENT, handleGontijoSessionUpdate);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onStorage = (event) => dispatch(syncGontijoSessionFromOtherTab(event));

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch, enabled]);

  useEffect(() => {
    // Once per load, and only for a session there is something to confirm. Guarding on the ref
    // rather than on the token alone keeps a token that changes mid-session — a re-login — from
    // queueing a second boot check it does not need.
    if (!enabled || !session.isHydrated || !session.token || hasCheckedAtBoot.current) return;

    hasCheckedAtBoot.current = true;
    dispatch(revalidateGontijoSession());
  }, [dispatch, enabled, session.isHydrated, session.token]);

  /**
   * Closes the gate by itself when the session runs out.
   *
   * Everything that reads the session computes "expired" from the clock, but nothing re-renders
   * when a clock passes a deadline: `useSelector` only re-runs on a store change. A buyer who
   * arrived signed in and left the checkout open past the expiry kept a live pay button and a gate
   * that had quietly stepped aside. This dispatch at the deadline is what makes the UI notice.
   *
   * Not scheduled for an expiry further out than a timer can hold (~24 days), which `setTimeout`
   * would otherwise fire immediately by overflow. A session that outlasts that will not expire
   * during a checkout, and the guards read the clock at payment time regardless.
   */
  useEffect(() => {
    if (!enabled || !session.token || session.expiresAt === null) return undefined;

    const delay = session.expiresAt - EXPIRY_BUFFER_MS - Date.now();
    if (delay > MAX_TIMER_MS) return undefined;

    const timer = setTimeout(() => dispatch(revalidateGontijoSession()), Math.max(delay, 0));
    return () => clearTimeout(timer);
  }, [dispatch, enabled, session.token, session.expiresAt]);

  const establish = useCallback(
    (token, expires, email) => dispatch(establishGontijoSession(token, expires, email)),
    [dispatch],
  );

  const signOut = useCallback(() => dispatch(signOutGontijoSession()), [dispatch]);

  const revalidate = useCallback(() => dispatch(revalidateGontijoSession()), [dispatch]);

  const value = useMemo(
    () => ({
      enabled,
      token: session.token,
      expiresAt: session.expiresAt,
      email: session.email,
      isAuthenticated: session.isAuthenticated,
      isHydrated: session.isHydrated,
      isValidating: session.isValidating,
      establish,
      signOut,
      revalidate,
    }),
    [enabled, session, establish, signOut, revalidate],
  );

  return <GontijoSessionContext.Provider value={value}>{children}</GontijoSessionContext.Provider>;
}

GontijoSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * The Gontijo session, for components. The only way they should read it.
 * @returns {object} The session and the actions that change it.
 */
export function useGontijoSessionContext() {
  const context = useContext(GontijoSessionContext);

  if (!context) {
    throw new Error('useGontijoSession must be used within a GontijoSessionProvider');
  }

  return context;
}
